import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import { toast } from "sonner";
import { Settings, Plus, Trash2 } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[var(--gold)]/50";

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/config", cfg); toast.success("Program rules saved"); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!cfg) return <><PageTitle title="Program Rules" /><div className="skeleton h-40" /></>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const setTarget = (i, key, v) => { const t = [...cfg.targets]; t[i] = { ...t[i], [key]: Number(v) }; set("targets", t); };
  const addTarget = () => set("targets", [...cfg.targets, { target: 0, instant: 0, salary: 0 }]);
  const delTarget = (i) => set("targets", cfg.targets.filter((_, x) => x !== i));
  const setBoost = (i, key, v) => { const b = [...cfg.boost_levels]; b[i] = { ...b[i], [key]: Number(v) }; set("boost_levels", b); };
  const setRank = (i, key, v) => { const r = [...cfg.ranks]; r[i] = { ...r[i], [key]: key === "name" || key === "color" ? v : Number(v) }; set("ranks", r); };

  return (
    <div className="space-y-6">
      <PageTitle title="Program Rules" subtitle="Configure the entire compensation engine" right={<LuxuryButton onClick={save} disabled={busy} data-testid="config-save">Save Changes</LuxuryButton>} />

      <GlassCard className="p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold text-white"><Settings size={18} className="text-[var(--gold)]" /> Core Rates (%)</h3>
        <div className="grid gap-4 sm:grid-cols-4">
          <Num label="Base Rate" v={cfg.base_rate} on={(v) => set("base_rate", Number(v))} tid="cfg-base" />
          <Num label="Direct Bonus" v={cfg.direct_bonus_pct} on={(v) => set("direct_bonus_pct", Number(v))} tid="cfg-direct" />
          <Num label="Binary Matching" v={cfg.matching_pct} on={(v) => set("matching_pct", Number(v))} tid="cfg-matching" />
          <Num label="Withdrawal Fee" v={cfg.withdrawal_fee_pct} on={(v) => set("withdrawal_fee_pct", Number(v))} />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Num label="Min Deposit" v={cfg.min_deposit} on={(v) => set("min_deposit", Number(v))} />
          <Num label="Min Withdrawal" v={cfg.min_withdrawal} on={(v) => set("min_withdrawal", Number(v))} />
          <Num label="Max Withdrawal" v={cfg.max_withdrawal} on={(v) => set("max_withdrawal", Number(v))} />
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-[var(--text-dim)]">
          <input type="checkbox" checked={cfg.roi_enabled} onChange={(e) => set("roi_enabled", e.target.checked)} data-testid="cfg-roi" />
          Enable performance/ROI engine (disabled by default until legal/compliance review is confirmed)
        </label>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">Boost Levels (not cumulative — highest qualified applies)</h3>
        <div className="space-y-2">
          {cfg.boost_levels.map((b, i) => (
            <div key={i} className="grid grid-cols-4 gap-3">
              <Num label={`L${b.level} Directs`} v={b.directs} on={(v) => setBoost(i, "directs", v)} />
              <Num label="Within Hours" v={b.hours} on={(v) => setBoost(i, "hours", v)} />
              <Num label="Rate %" v={b.rate} on={(v) => setBoost(i, "rate", v)} />
            </div>
          ))}
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-semibold text-white">Business Targets · Instant Rewards · Salary</h3>
          <LuxuryButton size="sm" variant="dark" onClick={addTarget} data-testid="cfg-add-target"><Plus size={14} className="mr-1 inline" /> Add Level</LuxuryButton>
        </div>
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-3 px-1 text-xs text-[var(--text-dim)]"><span>Target $</span><span>Instant $</span><span>Salary $/day</span><span></span></div>
          {cfg.targets.map((t, i) => (
            <div key={i} className="grid grid-cols-4 gap-3">
              <input className={inp} type="number" value={t.target} onChange={(e) => setTarget(i, "target", e.target.value)} />
              <input className={inp} type="number" value={t.instant} onChange={(e) => setTarget(i, "instant", e.target.value)} />
              <input className={inp} type="number" step="any" value={t.salary} onChange={(e) => setTarget(i, "salary", e.target.value)} />
              <button onClick={() => delTarget(i)} className="text-rose-400"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">Ranks</h3>
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-3 px-1 text-xs text-[var(--text-dim)]"><span>Name</span><span>Matched $</span><span>Directs</span><span>Color</span></div>
          {cfg.ranks.map((r, i) => (
            <div key={i} className="grid grid-cols-4 gap-3">
              <input className={inp} value={r.name} onChange={(e) => setRank(i, "name", e.target.value)} />
              <input className={inp} type="number" value={r.matched} onChange={(e) => setRank(i, "matched", e.target.value)} />
              <input className={inp} type="number" value={r.directs} onChange={(e) => setRank(i, "directs", e.target.value)} />
              <input className={inp} value={r.color} onChange={(e) => setRank(i, "color", e.target.value)} />
            </div>
          ))}
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">Compliance Configuration</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {["jurisdiction", "legal_entity", "terms_url", "risk_disclosure", "refund_policy"].map((k) => (
            <div key={k}>
              <p className="mb-1.5 text-xs uppercase tracking-wider text-[var(--text-dim)]">{k.replace(/_/g, " ")}</p>
              <input className={inp} value={cfg.compliance?.[k] || ""} onChange={(e) => set("compliance", { ...cfg.compliance, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-6">
          <label className="flex items-center gap-2 text-sm text-[var(--text-dim)]"><input type="checkbox" checked={cfg.compliance?.kyc_required} onChange={(e) => set("compliance", { ...cfg.compliance, kyc_required: e.target.checked })} /> KYC required</label>
          <label className="flex items-center gap-2 text-sm text-[var(--text-dim)]"><input type="checkbox" checked={cfg.compliance?.aml_required} onChange={(e) => set("compliance", { ...cfg.compliance, aml_required: e.target.checked })} /> AML review required</label>
        </div>
      </GlassCard>

      <div className="flex justify-end"><LuxuryButton onClick={save} disabled={busy}>Save Changes</LuxuryButton></div>
    </div>
  );
}

function Num({ label, v, on, tid }) {
  return (
    <div>
      <p className="mb-1.5 text-xs uppercase tracking-wider text-[var(--text-dim)]">{label}</p>
      <input className={inp} type="number" step="any" value={v} onChange={(e) => on(e.target.value)} data-testid={tid} />
    </div>
  );
}
